import { buildSquircleClipPath } from './build-squircle-clip-path';
import { SQUIRCLE_SURFACE_SELECTOR, resolveSquircleCornerRadius } from './squircle-radius';

const CLIP_ATTR = 'data-squircle-enhanced';

function applyClip(el: HTMLElement): void {
    const cornerRadius = resolveSquircleCornerRadius(el);
    if (cornerRadius === null) {
        if (el.getAttribute(CLIP_ATTR) === 'true') {
            el.style.clipPath = '';
            el.removeAttribute(CLIP_ATTR);
        }
        return;
    }

    const { width, height } = el.getBoundingClientRect();
    const clipPath = buildSquircleClipPath({ width, height, cornerRadius });
    if (!clipPath) {
        return;
    }

    el.style.clipPath = clipPath;
    el.setAttribute(CLIP_ATTR, 'true');
}

/**
 * Observes the document for lg+ radius surfaces and applies G2 squircle clips.
 * Mount once near the app root (e.g. via {@link SquircleEnhancer}).
 */
export function startSquircleEnhancer(root: ParentNode = document): () => void {
    if (typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
        return () => undefined;
    }

    const observed = new Set<HTMLElement>();

    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            if (entry.target instanceof HTMLElement) {
                applyClip(entry.target);
            }
        }
    });

    const watch = (el: Element): void => {
        if (!(el instanceof HTMLElement) || observed.has(el)) {
            return;
        }
        if (resolveSquircleCornerRadius(el) === null) {
            return;
        }
        observed.add(el);
        resizeObserver.observe(el);
        applyClip(el);
    };

    const unwatch = (el: Element): void => {
        if (!(el instanceof HTMLElement) || !observed.has(el)) {
            return;
        }
        observed.delete(el);
        resizeObserver.unobserve(el);
        if (el.getAttribute(CLIP_ATTR) === 'true') {
            el.style.clipPath = '';
            el.removeAttribute(CLIP_ATTR);
        }
    };

    const scan = (node: ParentNode): void => {
        if (node instanceof Element) {
            watch(node);
        }
        node.querySelectorAll?.(SQUIRCLE_SURFACE_SELECTOR).forEach(watch);
    };

    scan(root instanceof Document ? root.body : root);

    const mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof Element || node instanceof DocumentFragment) {
                        scan(node);
                    }
                });
                mutation.removedNodes.forEach((node) => {
                    if (node instanceof Element) {
                        unwatch(node);
                        node.querySelectorAll?.(SQUIRCLE_SURFACE_SELECTOR).forEach(unwatch);
                    }
                });
            }
            if (mutation.type === 'attributes' && mutation.target instanceof Element) {
                const el = mutation.target;
                if (resolveSquircleCornerRadius(el) === null) {
                    unwatch(el);
                } else {
                    watch(el);
                    applyClip(el as HTMLElement);
                }
            }
        }
    });

    mutationObserver.observe(root instanceof Document ? root.body : (root as Node), {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-squircle-radius', 'data-squircle-managed']
    });

    return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        observed.forEach((el) => {
            if (el.getAttribute(CLIP_ATTR) === 'true') {
                el.style.clipPath = '';
                el.removeAttribute(CLIP_ATTR);
            }
        });
        observed.clear();
    };
}
